import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import Copyright from '../blocks/copyright';

const API_BASE = 'https://fx.softmakon.uz/api';

const C = {
    bg:       '#f1f5f9',
    white:    '#ffffff',
    border:   '#e2e8f0',
    text:     '#0f172a',
    muted:    '#475569',
    dim:      '#94a3b8',
    blue:     '#2563eb',
    blueSoft: '#eff6ff',
    green:    '#15803d',
    greenSoft:'#f0fdf4',
    amber:    '#92400e',
    amberSoft:'#fffbeb',
    red:      '#b91c1c',
    redSoft:  '#fef2f2',
    purple:   '#6d28d9',
    purpleSoft:'#f5f3ff',
    teal:     '#0f766e',
    tealSoft: '#f0fdfa',
};

/* ─── Spinner ─────────────────────────────────────────────────────────────── */
function Spinner({ size = 18, color = C.blue }) {
    return (
        <svg style={{ animation: 'avSpin .75s linear infinite', display: 'inline-block', verticalAlign: 'middle' }}
             width={size} height={size} viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#e2e8f0" strokeWidth="3" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth="3" strokeLinecap="round" />
            <style>{`@keyframes avSpin{to{transform:rotate(360deg)}}`}</style>
        </svg>
    );
}

/* ─── Btn ──────────────────────────────────────────────────────────────────── */
function Btn({ children, onClick, href, color = C.blue, solid, style = {}, disabled, target, download }) {
    const base = {
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '9px 18px', borderRadius: 8,
        fontSize: 14, fontWeight: 500, lineHeight: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: solid ? 'none' : `1.5px solid ${color}40`,
        background: solid ? color : `${color}12`,
        color: solid ? '#fff' : color,
        textDecoration: 'none',
        opacity: disabled ? 0.5 : 1,
        transition: 'filter .15s',
        whiteSpace: 'nowrap',
        ...style,
    };
    if (href) {
        return (
            <a href={href} style={base} target={target} rel="noreferrer" download={download}>
                {children}
            </a>
        );
    }
    return (
        <button style={{ ...base, outline: 'none' }} onClick={onClick} disabled={disabled}>
            {children}
        </button>
    );
}

/* ─── Badge ───────────────────────────────────────────────────────────────── */
function Badge({ children, color, soft }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '5px 12px', borderRadius: 20,
            fontSize: 13, fontWeight: 600,
            color, background: soft, border: `1px solid ${color}30`,
        }}>
            {children}
        </span>
    );
}

/* ─── Page wrapper — footer always at bottom ──────────────────────────────── */
function Page({ children }) {
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', minHeight: '100vh',
            background: C.bg, fontFamily: 'system-ui,-apple-system,Segoe UI,sans-serif',
            color: C.text, fontSize: 15,
        }}>
            <div style={{ flex: 1 }}>{children}</div>
            <div style={{ borderTop: `1px solid ${C.border}`, background: C.white, padding: '14px 36px' }}>
                <Copyright />
            </div>
        </div>
    );
}

/* ─── Icons (16×16 SVG) ───────────────────────────────────────────────────── */
const ic = (d) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
         style={{ flexShrink: 0 }}>
        {d}
    </svg>
);
const Ic = {
    back:     ic(<><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></>),
    refresh:  ic(<><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></>),
    invoice:  ic(<><path d="M14 2H6a2 2 0 0 0-2 2v16l4-2 4 2 4-2 4 2V8z"/><polyline points="14 2 14 8 20 8"/></>),
    eye:      ic(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>),
    check:    ic(<polyline points="20 6 9 17 4 12" strokeWidth="2.5"/>),
    clock:    ic(<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>),
    trash:    ic(<><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></>),
    generate: ic(<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>),
    excel:    ic(<><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></>),
    pdf:      ic(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>),
    qr:       ic(<><rect x="3" y="3" width="5" height="5"/><rect x="16" y="3" width="5" height="5"/><rect x="3" y="16" width="5" height="5"/><rect x="4" y="4" width="3" height="3" fill="currentColor"/><rect x="17" y="4" width="3" height="3" fill="currentColor"/><rect x="4" y="17" width="3" height="3" fill="currentColor"/></>),
    dl:       ic(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>),
    close:    ic(<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>),
    box:      ic(<><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></>),
};

/* ─── Modal ───────────────────────────────────────────────────────────────── */
function Modal({ open, onClose, children }) {
    if (!open) return null;
    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div onClick={e => e.stopPropagation()} style={{
                background: C.white, borderRadius: 14,
                boxShadow: '0 25px 70px rgba(0,0,0,0.2)',
                overflow: 'hidden', minWidth: 380, maxWidth: '95vw',
            }}>
                {children}
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════════════════ */
function AviaInvoices() {
    const navigate   = useNavigate();
    const { serial } = useParams();
    return serial
        ? <AviaInvoiceDetail serial={serial} navigate={navigate} />
        : <AviaInvoiceList   navigate={navigate} />;
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  LIST VIEW                                                                  */
/* ══════════════════════════════════════════════════════════════════════════ */
function AviaInvoiceList({ navigate }) {
    const [serials,    setSerials]    = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [generating, setGenerating] = useState(null);
    const [deleting,   setDeleting]   = useState(null);
    const [confirmDel, setConfirmDel] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await fetch(`${API_BASE}/avia-invoices/`).then(r => r.json());
            setSerials(Array.isArray(d) ? d : []);
        } catch { toast.error("Ma'lumot olishda xatolik"); }
        finally  { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const doGenerate = async (sn) => {
        setGenerating(sn);
        try {
            const d = await fetch(`${API_BASE}/avia-invoices/generate/${sn}/`, { method: 'POST' }).then(r => r.json());
            if (d.success) { toast.success(`${d.boxes_generated} ta invoice yaratildi`); load(); }
            else toast.error(d.error || 'Xatolik');
        } catch { toast.error("Server bilan bog'lanib bo'lmadi"); }
        finally  { setGenerating(null); }
    };

    const doDelete = async (sn) => {
        setConfirmDel(null); setDeleting(sn);
        try {
            const d = await fetch(`${API_BASE}/avia-invoices/delete/${sn}/`, { method: 'DELETE' }).then(r => r.json());
            if (d.success) { toast.success("O'chirildi"); load(); }
            else toast.error(d.error || 'Xatolik');
        } catch { toast.error("Server xatolik"); }
        finally  { setDeleting(null); }
    };

    const gen    = serials.filter(s =>  s.is_generated).length;
    const pend   = serials.filter(s => !s.is_generated).length;
    const totalKg = serials.reduce((a, s) => a + parseFloat(s.total_weight || 0), 0).toFixed(1);

    const thStyle = (center) => ({
        padding: '13px 20px',
        textAlign: center ? 'center' : 'left',
        fontSize: 12, fontWeight: 700, color: C.muted,
        textTransform: 'uppercase', letterSpacing: '.6px',
        borderBottom: `2px solid ${C.border}`,
        background: C.bg, whiteSpace: 'nowrap',
    });

    const tdStyle = (center) => ({
        padding: '15px 20px',
        textAlign: center ? 'center' : 'left',
        borderBottom: `1px solid ${C.border}`,
        verticalAlign: 'middle',
    });

    return (
        <Page>
            {/* Header */}
            <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: '0 36px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 70 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 10, background: C.blueSoft, color: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {Ic.invoice}
                        </div>
                        <div>
                            <div style={{ fontSize: 20, fontWeight: 700 }}>Avia Invoice</div>
                            <div style={{ fontSize: 13, color: C.muted }}>Partiyalar boshqaruvi</div>
                        </div>
                    </div>
                    <Btn onClick={load} disabled={loading} color={C.muted} style={{ border: `1.5px solid ${C.border}`, background: C.white }}>
                        {loading ? <Spinner size={16} /> : Ic.refresh} Yangilash
                    </Btn>
                </div>
            </div>

            <div style={{ padding: '28px 36px' }}>

                {/* Stat cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
                    {[
                        { label: 'Jami partiya',  val: serials.length, color: C.blue,   soft: C.blueSoft   },
                        { label: 'Tayyor',         val: gen,            color: C.green,  soft: C.greenSoft  },
                        { label: 'Kutilmoqda',     val: pend,           color: C.amber,  soft: C.amberSoft  },
                        { label: 'Jami vazn (kg)', val: totalKg,        color: C.purple, soft: C.purpleSoft },
                    ].map((s, i) => (
                        <div key={i} style={{
                            background: C.white, border: `1px solid ${C.border}`,
                            borderRadius: 12, padding: '20px 22px',
                            display: 'flex', alignItems: 'center', gap: 16,
                        }}>
                            <div style={{
                                fontSize: 28, fontWeight: 800, color: s.color,
                                width: 56, height: 56, borderRadius: 12,
                                background: s.soft, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                {s.val}
                            </div>
                            <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.4 }}>{s.label}</div>
                        </div>
                    ))}
                </div>

                {/* Table */}
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 17, fontWeight: 700 }}>Partiyalar ro'yxati</span>
                        <span style={{ fontSize: 14, color: C.muted }}>{serials.length} ta yozuv</span>
                    </div>

                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, height: 240, color: C.muted }}>
                            <Spinner size={24} /> Yuklanmoqda...
                        </div>
                    ) : serials.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '80px 0', color: C.dim, fontSize: 15 }}>
                            Partiyalar topilmadi
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                <tr>
                                    <th style={thStyle(false)}>Partiya №</th>
                                    <th style={thStyle(false)}>Sana oralig'i</th>
                                    <th style={thStyle(true)}>Trek</th>
                                    <th style={thStyle(true)}>Karobka</th>
                                    <th style={thStyle(true)}>Vazn</th>
                                    <th style={thStyle(true)}>Holat</th>
                                    <th style={thStyle(false)}>Amallar</th>
                                </tr>
                                </thead>
                                <tbody>
                                {serials.map((s) => {
                                    const isGen  = s.is_generated;
                                    const isBusy = generating === s.serial_number || deleting === s.serial_number;
                                    return (
                                        <tr key={s.id}
                                            style={{ transition: 'background .1s' }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                            <td style={tdStyle(false)}>
                                                    <span
                                                        onClick={() => isGen && navigate(`/avia-invoices/${s.serial_number}`)}
                                                        style={{ fontWeight: 700, fontSize: 16, color: isGen ? C.blue : C.text, cursor: isGen ? 'pointer' : 'default' }}>
                                                        {s.serial_number}
                                                    </span>
                                            </td>
                                            <td style={{ ...tdStyle(false), color: C.muted, fontSize: 14 }}>
                                                {s.datefrom} — {s.dateto}
                                            </td>
                                            <td style={{ ...tdStyle(true), fontWeight: 700, fontSize: 16 }}>{s.items_count}</td>
                                            <td style={{ ...tdStyle(true), fontWeight: 700, fontSize: 16 }}>{s.boxes_count}</td>
                                            <td style={{ ...tdStyle(true), color: C.muted }}>
                                                {parseFloat(s.total_weight || 0).toFixed(1)} kg
                                            </td>
                                            <td style={tdStyle(true)}>
                                                {isGen
                                                    ? <Badge color={C.green} soft={C.greenSoft}>{Ic.check} Tayyor</Badge>
                                                    : <Badge color={C.amber} soft={C.amberSoft}>{Ic.clock} Kutilmoqda</Badge>}
                                            </td>
                                            <td style={tdStyle(false)}>
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                                    {isGen ? (
                                                        <>
                                                            <Btn onClick={() => navigate(`/avia-invoices/${s.serial_number}`)} disabled={isBusy} color={C.blue}>
                                                                {Ic.eye} Ko'rish
                                                            </Btn>
                                                            <Btn href={`${API_BASE}/avia-invoices/download/${s.serial_number}/manifest/`} target="_blank" download color={C.green}>
                                                                {Ic.excel} Manifest
                                                            </Btn>
                                                            <Btn href={`${API_BASE}/avia-invoices/download/${s.serial_number}/electron/`} target="_blank" download color={C.teal}>
                                                                {Ic.excel} Elektron
                                                            </Btn>
                                                            <Btn onClick={() => setConfirmDel(s.serial_number)} disabled={isBusy} color={C.red} style={{ padding: '9px 12px' }}>
                                                                {deleting === s.serial_number ? <Spinner size={16} color={C.red} /> : Ic.trash}
                                                            </Btn>
                                                        </>
                                                    ) : (
                                                        <Btn onClick={() => doGenerate(s.serial_number)} disabled={isBusy} solid>
                                                            {generating === s.serial_number
                                                                ? <><Spinner size={16} color="#fff" /> Yaratilmoqda...</>
                                                                : <>{Ic.generate} Generatsiya</>}
                                                        </Btn>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete confirm modal */}
            <Modal open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)}>
                <div style={{ padding: '24px 28px', borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Invoyslarni o'chirish</div>
                    <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.7 }}>
                        <strong style={{ color: C.text }}>{confirmDel}</strong> partiyasining barcha invoice fayllari
                        o'chiriladi. Trek kodlar saqlanib qoladi, kerak bo'lsa qayta generatsiya qilish mumkin.
                    </div>
                </div>
                <div style={{ padding: '16px 28px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <Btn onClick={() => setConfirmDel(null)} color={C.muted} style={{ border: `1.5px solid ${C.border}`, background: C.white }}>
                        Bekor qilish
                    </Btn>
                    <Btn onClick={() => doDelete(confirmDel)} solid color={C.red}>
                        {Ic.trash} O'chirish
                    </Btn>
                </div>
            </Modal>
        </Page>
    );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  DETAIL VIEW                                                                */
/* ══════════════════════════════════════════════════════════════════════════ */
function AviaInvoiceDetail({ serial, navigate }) {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    const [qrModal, setQrModal] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API_BASE}/avia-invoices/boxes/${serial}/`);
            if (!r.ok) throw new Error();
            setData(await r.json());
        } catch { toast.error("Ma'lumot olishda xatolik"); }
        finally  { setLoading(false); }
    }, [serial]);

    useEffect(() => { load(); }, [load]);

    if (loading) return (
        <Page>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, height: 320, color: C.muted }}>
                <Spinner size={28} /> Yuklanmoqda...
            </div>
        </Page>
    );

    if (!data) return null;

    const boxGroups = {};
    (data.boxes || []).forEach(b => {
        const k = b.box_number || '1';
        if (!boxGroups[k]) boxGroups[k] = [];
        boxGroups[k].push(b);
    });
    const boxKeys = Object.keys(boxGroups).sort((a, b) => (parseInt(a) || 0) - (parseInt(b) || 0));

    const thS = (c) => ({
        padding: '12px 20px', textAlign: c ? 'center' : 'left',
        fontSize: 12, fontWeight: 700, color: C.muted,
        textTransform: 'uppercase', letterSpacing: '.6px',
        borderBottom: `2px solid ${C.border}`, background: C.bg,
    });
    const tdS = (c) => ({
        padding: '14px 20px', textAlign: c ? 'center' : 'left',
        borderBottom: `1px solid ${C.border}`, verticalAlign: 'middle',
    });

    return (
        <Page>
            {/* Header */}
            <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: '0 36px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 70 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <Btn onClick={() => navigate('/avia-invoices')} color={C.muted} style={{ border: `1.5px solid ${C.border}`, background: C.white }}>
                            {Ic.back} Orqaga
                        </Btn>
                        <div style={{ width: 1, height: 28, background: C.border }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 44, height: 44, borderRadius: 10, background: C.blueSoft, color: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {Ic.invoice}
                            </div>
                            <div>
                                <div style={{ fontSize: 20, fontWeight: 700 }}>{serial}</div>
                                <div style={{ fontSize: 13, color: C.muted }}>
                                    {data.total_items} trek · {data.boxes_count} karobka · {parseFloat(data.total_weight || 0).toFixed(1)} kg
                                </div>
                            </div>
                        </div>
                    </div>
                    <Btn onClick={load} color={C.muted} style={{ border: `1.5px solid ${C.border}`, background: C.white }}>
                        {Ic.refresh} Yangilash
                    </Btn>
                </div>
            </div>

            <div style={{ padding: '28px 36px' }}>

                {/* Download panel */}
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 16 }}>
                        Umumiy yuklab olish
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <Btn href={`${API_BASE}/avia-invoices/download/${serial}/manifest/`} target="_blank" download color={C.green}>
                            {Ic.excel} Manifest Excel
                        </Btn>
                        <Btn href={`${API_BASE}/avia-invoices/download/${serial}/electron/`} target="_blank" download color={C.teal}>
                            {Ic.excel} Elektronka Excel
                        </Btn>
                        <Btn href={`${API_BASE}/avia-invoices/download/${serial}/combined-pdf/`} target="_blank" download color={C.red}>
                            {Ic.pdf} Barcha PDF
                        </Btn>
                        <Btn href={`${API_BASE}/avia-invoices/download/${serial}/combined-qr/`} target="_blank" download color={C.purple}>
                            {Ic.qr} Barcha QR
                        </Btn>
                    </div>
                </div>

                {/* Boxes */}
                {boxKeys.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '80px 0', color: C.dim }}>Karobkalar topilmadi</div>
                ) : boxKeys.map(boxKey => {
                    const list     = boxGroups[boxKey];
                    const totalKg  = list.reduce((s, b) => s + parseFloat(b.total_weight || 0), 0);
                    const totalTrek = list.reduce((s, b) => s + (b.items_count || 0), 0);

                    return (
                        <div key={boxKey} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 18, overflow: 'hidden' }}>

                            {/* Box header */}
                            <div style={{
                                background: C.blueSoft, borderBottom: `1px solid #bfdbfe`,
                                padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                    <div style={{
                                        width: 44, height: 44, borderRadius: 10, background: C.blue, color: '#fff',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 800, fontSize: 16,
                                    }}>
                                        {boxKey}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: 16, color: C.blue }}>Box {boxKey}</div>
                                        <div style={{ fontSize: 13, color: '#3b82f6' }}>
                                            {list.length} mijoz · {totalKg.toFixed(2)} kg · {totalTrek} trek kod
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <Btn href={`${API_BASE}/avia-invoices/download/${serial}/box/${boxKey}/pdf/`} target="_blank" download color={C.red}>
                                        {Ic.pdf} Box PDF
                                    </Btn>
                                    <Btn href={`${API_BASE}/avia-invoices/download/${serial}/box/${boxKey}/qr/`} target="_blank" download color={C.purple}>
                                        {Ic.qr} Box QR
                                    </Btn>
                                </div>
                            </div>

                            {/* Clients table */}
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                <tr>
                                    <th style={thS(false)}>Mijoz kodi</th>
                                    <th style={thS(false)}>Invoice ID</th>
                                    <th style={thS(true)}>Trek soni</th>
                                    <th style={thS(true)}>Vazn</th>
                                    <th style={thS(true)}>PDF</th>
                                    <th style={thS(true)}>QR</th>
                                </tr>
                                </thead>
                                <tbody>
                                {list.map((b) => (
                                    <tr key={b.id}
                                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <td style={tdS(false)}>
                                            <span style={{ fontWeight: 700, fontSize: 16 }}>{b.client_code}</span>
                                        </td>
                                        <td style={tdS(false)}>
                                                <span style={{
                                                    fontSize: 13, color: C.muted,
                                                    background: C.bg, border: `1px solid ${C.border}`,
                                                    borderRadius: 6, padding: '4px 10px',
                                                }}>
                                                    {b.invoice_id}
                                                </span>
                                        </td>
                                        <td style={tdS(true)}>
                                                <span style={{
                                                    display: 'inline-block', background: C.blueSoft, color: C.blue,
                                                    border: `1px solid #bfdbfe`, borderRadius: 20,
                                                    padding: '4px 14px', fontSize: 14, fontWeight: 600,
                                                }}>
                                                    {b.items_count}
                                                </span>
                                        </td>
                                        <td style={{ ...tdS(true), color: C.muted }}>
                                            {parseFloat(b.total_weight).toFixed(2)} kg
                                        </td>
                                        <td style={tdS(true)}>
                                            {b.pdf_url
                                                ? <Btn href={b.pdf_url} target="_blank" color={C.red}>{Ic.dl} PDF</Btn>
                                                : <span style={{ color: C.dim }}>—</span>}
                                        </td>
                                        <td style={tdS(true)}>
                                            {b.qr_url
                                                ? <Btn onClick={() => setQrModal({ url: b.qr_url, label: b.client_code, box: b.box_number, invoice: b.invoice_id })} color={C.purple}>
                                                    {Ic.eye} Ko'rish
                                                </Btn>
                                                : <span style={{ color: C.dim }}>—</span>}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                                <tfoot>
                                <tr style={{ background: C.bg, borderTop: `2px solid ${C.border}` }}>
                                    <td colSpan={2} style={{ padding: '12px 20px', fontSize: 13, color: C.muted }}>{list.length} ta mijoz</td>
                                    <td style={{ padding: '12px 20px', textAlign: 'center', fontWeight: 700, fontSize: 15 }}>{totalTrek}</td>
                                    <td style={{ padding: '12px 20px', textAlign: 'center', fontWeight: 700, fontSize: 15, color: C.green }}>
                                        {totalKg.toFixed(2)} kg
                                    </td>
                                    <td colSpan={2} />
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    );
                })}
            </div>

            {/* QR Modal */}
            <Modal open={Boolean(qrModal)} onClose={() => setQrModal(null)}>
                <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontSize: 17, fontWeight: 700 }}>QR Kod</div>
                        {qrModal && <div style={{ fontSize: 13, color: C.muted }}>{qrModal.label} · Box {qrModal.box} · {qrModal.invoice}</div>}
                    </div>
                    <button onClick={() => setQrModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 6, display: 'flex' }}>
                        {Ic.close}
                    </button>
                </div>
                {qrModal && (
                    <>
                        <div style={{ padding: 24, textAlign: 'center' }}>
                            <img src={qrModal.url} alt="QR"
                                 style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 10, border: `1px solid ${C.border}` }} />
                        </div>
                        <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'center' }}>
                            <Btn href={qrModal.url} download target="_blank" solid>
                                {Ic.dl} Yuklab olish
                            </Btn>
                            <Btn onClick={() => setQrModal(null)} color={C.muted} style={{ border: `1.5px solid ${C.border}`, background: C.white }}>
                                Yopish
                            </Btn>
                        </div>
                    </>
                )}
            </Modal>
        </Page>
    );
}

export default AviaInvoices;